import { useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { AlertCircle, Plus, Loader2, Pencil, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import type { GrGameRewardLimitResponse } from '@/lib/api';
import { useGameRewardLimits, useGameConfigs } from '../hooks';
import {
  DAILY_CAP_TIER_ORDER,
  DAILY_CAP_TIER_MULTIPLIERS,
  effectiveCapForTier,
} from '../lib/dailyCapTiers';

const ALL_GAMES = '__all__';

/**
 * Per-(game, reward type) daily caps — the RESERVED-SLICE axis.
 *
 * Deliberately a separate section from the reward-type limits above it rather
 * than extra columns on that table. The two are keyed differently and mean
 * different things: a reward-type limit is a SHARED pool every game draws from
 * (including third-party plugin arches), while these are RESERVED to one game.
 * Merging them into one grid would make a row's meaning depend on whether the
 * game column happened to be filled in.
 *
 * The stored number is the FREE-player cap; paid tiers scale up from it, so the
 * table shows what each tier actually gets rather than making an admin do the
 * arithmetic to find out what they just configured.
 */
export function GameRewardLimitsSection() {
  const [gameFilter, setGameFilter] = useState<string>(ALL_GAMES);
  const scopedGameId = gameFilter === ALL_GAMES ? undefined : gameFilter;

  const { limits, loading, error, refresh } = useGameRewardLimits(scopedGameId);
  const { gameConfigs } = useGameConfigs();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<GrGameRewardLimitResponse | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [gameConfigId, setGameConfigId] = useState('');
  const [rewardType, setRewardType] = useState('');
  const [maxPerDay, setMaxPerDay] = useState('100');

  const gameNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const config of gameConfigs) map.set(config.gameConfigId, config.gameName);
    return map;
  }, [gameConfigs]);

  const resetForm = () => {
    setEditing(null);
    setGameConfigId(scopedGameId ?? '');
    setRewardType('');
    setMaxPerDay('100');
    setFormError(null);
  };

  const openEdit = (limit: GrGameRewardLimitResponse) => {
    setEditing(limit);
    setGameConfigId(limit.gameConfigId);
    setRewardType(limit.rewardType);
    setMaxPerDay(String(limit.maxAmountPerDay));
    setFormError(null);
    setDialogOpen(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = Number(maxPerDay);
    if (!Number.isInteger(parsed) || parsed < 0) {
      setFormError('Cap must be a whole number of 0 or more.');
      return;
    }

    setSaving(true);
    setFormError(null);
    setNotice(null);
    try {
      if (editing) {
        const updated = await api.updateGameRewardsGameLimit(editing.limitId, {
          maxAmountPerDay: parsed,
        });
        // Loosening a cap re-opens review on the affected game. Say so rather
        // than letting an admin discover it when the game stops paying out.
        if (updated.gamesFlippedToPending) {
          setNotice(
            `Cap raised — ${updated.gamesFlippedToPending} game config returned to pending review and will not pay out until re-approved.`,
          );
        }
      } else {
        if (!gameConfigId || !rewardType.trim()) {
          setFormError('Pick a game and a reward type.');
          setSaving(false);
          return;
        }
        await api.createGameRewardsGameLimit({
          gameConfigId,
          rewardType: rewardType.trim(),
          maxAmountPerDay: parsed,
        });
      }
      setDialogOpen(false);
      resetForm();
      refresh();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to save cap');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (limitId: string) => {
    try {
      await api.deleteGameRewardsGameLimit(limitId);
      refresh();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to delete cap');
    }
  };

  const handleDialogChange = (open: boolean) => {
    setDialogOpen(open);
    if (!open) resetForm();
  };

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold">Per-Game Daily Caps</h2>
          <p className="text-sm text-muted-foreground">
            Reserved to one game. Nothing else can spend this budget. When a reward-type limit above
            also applies, the lower remaining wins.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={gameFilter} onValueChange={setGameFilter}>
            <SelectTrigger className="w-48" aria-label="Filter by game">
              <SelectValue placeholder="All games" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_GAMES}>All games</SelectItem>
              {gameConfigs.map((config) => (
                <SelectItem key={config.gameConfigId} value={config.gameConfigId}>
                  {config.gameName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Dialog open={dialogOpen} onOpenChange={handleDialogChange}>
            <DialogTrigger asChild>
              <Button size="sm" onClick={() => resetForm()}>
                <Plus className="h-4 w-4 mr-2" />
                Add Cap
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>{editing ? 'Edit Per-Game Cap' : 'Create Per-Game Cap'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSave} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="capGameConfig">Game *</Label>
                  <Select value={gameConfigId} onValueChange={setGameConfigId} disabled={!!editing}>
                    <SelectTrigger id="capGameConfig">
                      <SelectValue placeholder="Select a game" />
                    </SelectTrigger>
                    <SelectContent>
                      {gameConfigs.map((config) => (
                        <SelectItem key={config.gameConfigId} value={config.gameConfigId}>
                          {config.gameName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="capRewardType">Reward Type *</Label>
                  <Input
                    id="capRewardType"
                    value={rewardType}
                    onChange={(e) => setRewardType(e.target.value)}
                    placeholder="Heptal"
                    disabled={!!editing}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="capMaxPerDay">Daily Cap (free players)</Label>
                  <Input
                    id="capMaxPerDay"
                    type="number"
                    min={0}
                    step={1}
                    value={maxPerDay}
                    onChange={(e) => setMaxPerDay(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Total AMOUNT of this reward a free player can earn from this game per UTC day.
                    Paid tiers scale up:{' '}
                    {DAILY_CAP_TIER_ORDER.filter((t) => t !== 'free')
                      .map((t) => `${t} ${DAILY_CAP_TIER_MULTIPLIERS[t]}x`)
                      .join(', ')}
                    . Enter 0 for no cap.
                  </p>
                  {Number.isInteger(Number(maxPerDay)) && Number(maxPerDay) > 0 && (
                    <p className="text-xs text-muted-foreground">
                      {DAILY_CAP_TIER_ORDER.map(
                        (t) => `${t} ${effectiveCapForTier(Number(maxPerDay), t)}`,
                      ).join(' · ')}
                    </p>
                  )}
                </div>

                {formError && <p className="text-sm text-destructive">{formError}</p>}

                <Button type="submit" className="w-full" disabled={saving}>
                  {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {editing ? 'Update Cap' : 'Create Cap'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {notice && (
        <div className="flex items-center gap-2 rounded-md border border-amber-500 bg-amber-500/10 p-3">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <p className="text-sm">{notice}</p>
        </div>
      )}

      {(error || formError) && (
        <div className="flex items-center gap-2 rounded-md border border-destructive bg-destructive/10 p-3">
          <AlertCircle className="h-4 w-4 text-destructive" />
          <p className="text-sm text-destructive">{error || formError}</p>
        </div>
      )}

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-12 rounded-lg" />
          ))}
        </div>
      ) : limits.length === 0 ? (
        <p className="text-center text-muted-foreground py-8">
          No per-game caps configured. Rewards from every game are unlimited on this axis.
        </p>
      ) : (
        <Card>
          <CardContent className="pt-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Game</TableHead>
                  <TableHead>Reward Type</TableHead>
                  {DAILY_CAP_TIER_ORDER.map((tier) => (
                    <TableHead key={tier} className="capitalize">
                      {tier}
                    </TableHead>
                  ))}
                  <TableHead className="w-20" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {limits.map((limit) => {
                  const unlimited = limit.maxAmountPerDay === 0;
                  return (
                    <TableRow key={limit.limitId}>
                      <TableCell className="font-medium">
                        {gameNameById.get(limit.gameConfigId) ?? limit.gameConfigId}
                      </TableCell>
                      <TableCell>{limit.rewardType}</TableCell>
                      {DAILY_CAP_TIER_ORDER.map((tier) => (
                        <TableCell key={tier}>
                          {unlimited
                            ? tier === 'free'
                              ? 'Unlimited'
                              : '—'
                            : effectiveCapForTier(limit.maxAmountPerDay, tier)}
                        </TableCell>
                      ))}
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            aria-label={`Edit cap for ${limit.rewardType}`}
                            onClick={() => openEdit(limit)}
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                                aria-label={`Delete cap for ${limit.rewardType}`}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Per-Game Cap</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Removing this cap makes {limit.rewardType} unlimited from this
                                  game unless a reward-type limit still applies. This cannot be
                                  undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleDelete(limit.limitId)}>
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </section>
  );
}
